import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { InsightsState } from 'app-redux/InsightsState';
import * as dto from 'client/dto';
import { HistoryPanelFilterOptionsValue } from 'models/FilterOptions';
import { nameof } from 'utils/typeHelpers';

export type DiseaseActivityUpdatesState = {
  isLoading: boolean;
  hasError: boolean;
  isLoadingAbove: boolean;
  isLoadingBelow: boolean;
  data: dto.DailyActivityListModel;
  filter: HistoryPanelFilterOptionsValue;
  topDescriptionForSelectedUserLocation: dto.DiseaseActivityDescriptionsModel;
  topDescriptionForSelectedActivityCountry: dto.DiseaseActivityDescriptionsModel;
};

export const InitialDiseaseActivityUpdatesState: DiseaseActivityUpdatesState = {
  isLoading: false,
  hasError: false,
  isLoadingAbove: false,
  isLoadingBelow: false,
  data: {},
  filter: 'all-activity',
  topDescriptionForSelectedUserLocation: null,
  topDescriptionForSelectedActivityCountry: null,
};

const diseaseActivityUpdatesSlice = createSlice({
  name: nameof<InsightsState>('diseaseActivityUpdates'),
  initialState: InitialDiseaseActivityUpdatesState,
  reducers: {
    loadDiseaseActivityUpdatesStart: state => {
      state.isLoading = true;
      state.hasError = false;
    },
    loadDiseaseActivityUpdatesSuccess: (
      state,
      action: PayloadAction<dto.DailyActivityListModel>
    ) => {
      const data = action.payload;

      state.isLoading = false;
      state.hasError = false;
      state.data = data;
    },
    loadDiseaseActivityUpdatesError: state => {
      state.isLoading = false;
      state.hasError = true;
    },
    loadNewerDiseaseActivityUpdatesStart: state => {
      state.isLoadingAbove = true;
    },
    loadNewerDiseaseActivityUpdatesSuccess: (
      state,
      action: PayloadAction<dto.DailyActivityListModel>
    ) => {
      const data = action.payload;

      state.isLoadingAbove = false;
      state.hasError = false;
      state.data.dailyReports = [...(data?.dailyReports || []), ...(state.data.dailyReports || [])];
      state.data.hasNewerEntries = data.hasNewerEntries;
    },
    loadNewerDiseaseActivityUpdatesError: state => {
      state.isLoadingAbove = false;
      state.hasError = true;
    },
    loadOlderDiseaseActivityUpdatesStart: state => {
      state.isLoadingBelow = true;
    },
    loadOlderDiseaseActivityUpdatesSuccess: (
      state,
      action: PayloadAction<dto.DailyActivityListModel>
    ) => {
      const data = action.payload;

      state.isLoadingBelow = false;
      state.hasError = false;
      state.data.dailyReports = [
        ...(state.data?.dailyReports || []),
        ...(data?.dailyReports || []),
      ];
      state.data.hasOlderEntries = data.hasOlderEntries;
    },
    loadOlderDiseaseActivityUpdatesError: state => {
      state.isLoadingBelow = false;
      state.hasError = true;
    },
    setDiseaseActivityUpdatesFilter: (
      state,
      action: PayloadAction<HistoryPanelFilterOptionsValue>
    ) => {
      state.filter = action.payload;
    },
    setTopDescriptionForSelectedUserLocation: (
      state,
      action: PayloadAction<dto.DiseaseActivityDescriptionsModel>
    ) => {
      state.topDescriptionForSelectedUserLocation = action.payload;
    },
    setTopDescriptionForSelectedActivityCountry: (
      state,
      action: PayloadAction<dto.DiseaseActivityDescriptionsModel>
    ) => {
      state.topDescriptionForSelectedActivityCountry = action.payload;
    },
  },
});

const { actions, reducer } = diseaseActivityUpdatesSlice;
export const {
  loadDiseaseActivityUpdatesStart,
  loadDiseaseActivityUpdatesSuccess,
  loadDiseaseActivityUpdatesError,
  loadOlderDiseaseActivityUpdatesStart,
  loadOlderDiseaseActivityUpdatesSuccess,
  loadOlderDiseaseActivityUpdatesError,
  loadNewerDiseaseActivityUpdatesStart,
  loadNewerDiseaseActivityUpdatesSuccess,
  loadNewerDiseaseActivityUpdatesError,
  setDiseaseActivityUpdatesFilter,
  setTopDescriptionForSelectedUserLocation,
  setTopDescriptionForSelectedActivityCountry,
} = actions;
export default reducer;
